import type { CategoryChipData } from "@models/category";

interface BrowseCategoriesProps {
    /** Top-level categories only — the Browse hierarchy handles the rest. */
    categories: CategoryChipData[];
    isLoading: boolean;
    hasError: boolean;
    onSelect: (categoryId: number) => void;
    onRetry: () => void;
}

// Same chip styling as the results filter panel's unselected chip
// (FilterPanel.chipClass) so the overlay and the results page read as one.
const CHIP_CLASSES =
    "badge badge-lg cursor-pointer font-medium border gap-1 bg-primary/10 border-primary/10 text-neutral hover:bg-primary/20";

// The search overlay's empty state (#401): a fresh shopper has no recent
// searches, so without this the panel opened onto nothing. A loaded tree
// wins over the flags (a refetch in flight, or a later failure, must not
// hide chips that are already there); the flags only describe an EMPTY tree.
export const BrowseCategories = ({
    categories,
    isLoading,
    hasError,
    onSelect,
    onRetry,
}: BrowseCategoriesProps) => {
    const hasCategories = categories.length > 0;
    // Loaded and empty: the catalog itself has no categories — nothing to
    // offer, so nothing to show.
    if (!hasCategories && !isLoading && !hasError) return null;

    return (
        <div data-testid="browse-categories" className="px-4 py-3">
            <div className="text-sm font-bold text-neutral mb-2">
                Browse categories
            </div>
            {hasCategories ? (
                <div
                    role="group"
                    aria-label="Browse categories"
                    className="flex flex-wrap gap-2"
                >
                    {categories.map((category) => (
                        <button
                            key={category.id}
                            type="button"
                            onClick={() => onSelect(category.id)}
                            className={CHIP_CLASSES}
                        >
                            {category.name}
                        </button>
                    ))}
                </div>
            ) : isLoading ? (
                <span className="text-sm text-neutral/60">
                    Loading categories…
                </span>
            ) : (
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-neutral/60">
                        Couldn't load categories.
                    </span>
                    <button
                        type="button"
                        onClick={onRetry}
                        className="btn btn-link btn-xs px-0 text-primary"
                    >
                        Retry
                    </button>
                </div>
            )}
        </div>
    );
};
